import React, { useState, useEffect } from 'react';
import {useAdmin} from '@/contexts/AdminContext';import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Users, CreditCard, MessageSquare, TrendingUp, DollarSign, Activity, BookOpen, UserPlus, ArrowUp, ArrowDown, Minus, Gauge, Globe, Shield, ChevronDown, Zap, Timer} from 'lucide-react';import {PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, LineChart, Line, CartesianGrid} from 'recharts';import AdminLayout from './AdminLayout';
import GuestAnalyticsCard from './GuestAnalyticsCard';
import InstallSourcesCard from './InstallSourcesCard';
import {useNavigate} from 'react-router-dom';import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

function StatCard({ icon: Icon, label, value, sub, color = '#6366f1', onClick }) {
  const { isDark, colors } = useAdminTheme();
  return (
    <div
      className={`rounded-2xl p-5 transition-all ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg' : ''}`}
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-4.5 w-4.5" style={{ color }} />
        </div>
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: colors.text }}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{sub}</p>}
    </div>
  );
}

const NEW_USER_PERIODS = [
  { key: '3', label: '3 Days', field: 'last_3_days', prev: 'prev_3_days' },
  { key: '7', label: '7 Days', field: 'last_7_days', prev: 'prev_7_days' },
  { key: '30', label: '30 Days', field: 'last_30_days', prev: 'prev_30_days' },
];

function NewUsersCard({ newUsers }) {
  const { isDark, colors } = useAdminTheme();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('7');
  const [expanded, setExpanded] = useState(false);
  const [recentUsers, setRecentUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  if (!newUsers) return null;
  const selected = NEW_USER_PERIODS.find(p => p.key === period);
  const current = newUsers[selected.field] ?? 0;
  const prev = newUsers[selected.prev] ?? 0;
  const diff = prev > 0 ? Math.round(((current - prev) / prev) * 100) : current > 0 ? 100 : 0;
  const trend = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !recentUsers) {
      setLoadingUsers(true);
      try {
        const res = await axios.get(`${API}/admin/users/recent?days=${period}&limit=15`, { withCredentials: true });
        setRecentUsers(res.data.users || []);
      } catch { setRecentUsers([]); }
      setLoadingUsers(false);
    }
  };

  const refreshForPeriod = async (p) => {
    setPeriod(p);
    if (expanded) {
      setLoadingUsers(true);
      try {
        const res = await axios.get(`${API}/admin/users/recent?days=${p}&limit=15`, { withCredentials: true });
        setRecentUsers(res.data.users || []);
      } catch { setRecentUsers([]); }
      setLoadingUsers(false);
    }
  };

  const tierColors = { free: '#64748b', paid: '#6366f1', premium: '#8b5cf6', share: '#10b981', trial: '#f59e0b' };

  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }} data-testid="new-users-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 cursor-pointer" onClick={handleExpand}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <UserPlus className="h-4.5 w-4.5 text-emerald-400" />
          </div>
          <span className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>New Users</span>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} style={{ color: colors.textMuted }} />
        </div>
        <div className="flex gap-1 p-0.5 rounded-lg" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9' }} data-testid="new-users-period-filter">
          {NEW_USER_PERIODS.map(p => (
            <button key={p.key} onClick={() => refreshForPeriod(p.key)}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium transition-all"
              style={{ background: period === p.key ? 'rgba(16,185,129,0.2)' : 'transparent', color: period === p.key ? '#34d399' : colors.textMuted }}
              data-testid={`new-users-${p.key}d`}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-3 cursor-pointer" onClick={handleExpand}>
        <p className="text-3xl font-bold" style={{ color: colors.text }}>{current}</p>
        {prev > 0 && (
          <div className={`flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${trend === 'up' ? 'bg-emerald-500/15 text-emerald-400' : trend === 'down' ? 'bg-red-500/15 text-red-400' : 'bg-slate-500/15 text-slate-400'}`} data-testid="new-users-trend">
            {trend === 'up' ? <ArrowUp className="h-3 w-3" /> : trend === 'down' ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(diff)}%
          </div>
        )}
      </div>
      <p className="text-xs mt-1.5" style={{ color: colors.textMuted }}>vs previous {selected.label.toLowerCase()} · <span className="underline cursor-pointer" onClick={handleExpand}>{expanded ? 'hide details' : 'view details'}</span></p>

      {/* Expandable user list */}
      {expanded && (
        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.cardBorder}` }} data-testid="new-users-detail">
          {loadingUsers ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin h-5 w-5 border-2 border-emerald-400 border-t-transparent rounded-full" />
            </div>
          ) : recentUsers && recentUsers.length > 0 ? (
            <div className="space-y-1">
              <div className="flex items-center px-3 py-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                <span className="w-6" />
                <span className="flex-1 ml-3">User</span>
                <span className="w-16 text-center">Tier</span>
                <span className="w-16 text-center">Source</span>
                <span className="w-14 text-right">Msgs</span>
                <span className="w-20 text-right">Joined</span>
              </div>
              {recentUsers.map((u, i) => {
                const tier = u.is_trial ? 'trial' : (u.subscription_tier || 'free');
                const date = new Date(u.created_at);
                return (
                  <div key={i} className="flex items-center px-3 py-2 rounded-lg transition-all cursor-pointer"
                    style={{ ':hover': { background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' } }}
                    onClick={() => navigate(`/admin/users?search=${encodeURIComponent(u.email)}`)}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    data-testid={`recent-user-${i}`}>
                    <span className="text-base leading-none w-6 text-center shrink-0">{countryFlag(u.country_code)}</span>
                    <div className="flex-1 min-w-0 ml-3">
                      <span className="text-xs font-medium truncate block" style={{ color: colors.text }}>{u.name || 'Unnamed'}</span>
                      <span className="text-[11px] truncate block" style={{ color: colors.textMuted }}>{u.email}</span>
                    </div>
                    <span className="w-16 text-center">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium" style={{ background: `${tierColors[tier]}20`, color: tierColors[tier] }}>
                        {tier}
                      </span>
                    </span>
                    <span className="w-16 text-center">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md" style={{
                        background: u.auth_method === 'google' ? 'rgba(234,179,8,0.12)' : 'rgba(99,102,241,0.12)',
                        color: u.auth_method === 'google' ? '#eab308' : '#818cf8'
                      }}>
                        {u.auth_method === 'google' ? 'Google' : 'Email'}
                      </span>
                    </span>
                    <span className="w-14 text-right text-xs font-medium" style={{ color: u.messages_sent > 0 ? '#22c55e' : colors.textMuted }}>
                      {u.messages_sent}
                    </span>
                    <span className="w-20 text-right text-[11px]" style={{ color: colors.textMuted }}>
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                );
              })}
              {recentUsers.length >= 15 && (
                <button className="w-full text-center py-2 text-xs font-medium hover:underline" style={{ color: colors.accent }}
                  onClick={() => navigate('/admin/users')} data-testid="view-all-users-btn">
                  View all users
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-center py-4" style={{ color: colors.textMuted }}>No new users in this period</p>
          )}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children, icon: Icon, iconColor }) {
  const { isDark, colors } = useAdminTheme();
  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-4 w-4" style={{ color: iconColor || colors.accent }} />}
        <h3 className="text-sm font-medium" style={{ color: colors.textSecondary }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CustomTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
      <p className="font-medium mb-1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
}

function countryFlag(code) {
  if (!code) return '';
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt()));
}

function CountrySection({ countries, onCountryClick }) {
  const { isDark, colors } = useAdminTheme();
  if (!countries || countries.length === 0) {
    return (
      <ChartCard title="Users by Country" icon={Globe} iconColor="#10b981">
        <p className="text-xs" style={{ color: colors.textMuted }}>No country data yet. Data is captured at registration.</p>
      </ChartCard>
    );
  }
  const maxCount = countries[0]?.count || 1;
  return (
    <ChartCard title="Users by Country" icon={Globe} iconColor="#10b981">
      <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
        {countries.map((c, i) => (
          <div
            key={i}
            className="flex items-center gap-3 cursor-pointer rounded-lg px-2 py-1.5 transition-all"
            onClick={() => onCountryClick && onCountryClick(c.country)}
            onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            data-testid={`country-row-${c.code}`}
          >
            <span className="text-lg leading-none w-7 text-center">{countryFlag(c.code)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium truncate" style={{ color: colors.text }}>{c.country}</span>
                <span className="text-xs font-bold tabular-nums" style={{ color: colors.accent }}>{c.count}</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${(c.count / maxCount) * 100}%`, background: '#10b981' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </ChartCard>
  );
}

function UsageAnalyticsCard({ data, isDark, colors, axisColor }) {
  const { tier_stats, trend_14d, limits, today_at_limit, week_at_limit } = data;
  const tiers = [
    { key: 'free', label: 'Free', color: '#64748b', limitColor: '#94a3b8' },
    { key: 'paid', label: 'Standard', color: '#6366f1', limitColor: '#818cf8' },
    { key: 'premium', label: 'Premium', color: '#f59e0b', limitColor: '#fbbf24' },
  ];

  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }} data-testid="usage-analytics-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-emerald-400" />
          <h3 className="text-sm font-medium" style={{ color: colors.textSecondary }}>Message Limit Usage</h3>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="text-xs" style={{ color: colors.textMuted }}>Today at limit</span>
            <p className="text-lg font-bold" style={{ color: today_at_limit > 0 ? '#f59e0b' : colors.text }}>{today_at_limit}</p>
          </div>
          <div className="text-right">
            <span className="text-xs" style={{ color: colors.textMuted }}>This week</span>
            <p className="text-lg font-bold" style={{ color: colors.text }}>{week_at_limit}</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {/* Trend chart */}
        <div>
          <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Users Hitting Limit (14 days)</p>
          {(trend_14d || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={trend_14d}>
                <CartesianGrid stroke={isDark ? '#1e293b' : '#f1f5f9'} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: axisColor }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: axisColor }} width={25} allowDecimals={false} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Bar dataKey="users_at_limit" fill="#f59e0b" radius={[3, 3, 0, 0]} name="At Limit" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs py-8 text-center" style={{ color: colors.textMuted }}>No data yet</p>}
        </div>

        {/* Per-tier breakdown */}
        <div>
          <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Today by Tier</p>
          <div className="space-y-3">
            {tiers.map(t => {
              const stats = tier_stats?.[t.key];
              if (!stats) return null;
              const isUnlimited = stats.limit === -1;
              const pct = isUnlimited ? 0 : stats.total_users_active > 0 ? Math.round((stats.users_at_limit / stats.total_users_active) * 100) : 0;

              return (
                <div key={t.key} className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }} data-testid={`usage-tier-${t.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                      <span className="text-xs font-medium" style={{ color: colors.text }}>{t.label}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md" style={{ background: `${t.color}18`, color: t.limitColor }}>
                        {isUnlimited ? 'Unlimited' : `${stats.limit}/day`}
                      </span>
                    </div>
                    {!isUnlimited && stats.total_users_active > 0 && (
                      <span className="text-[11px] font-semibold" style={{ color: pct > 50 ? '#f59e0b' : colors.textMuted }}>{pct}% at limit</span>
                    )}
                  </div>
                  {isUnlimited ? (
                    <p className="text-[11px]" style={{ color: colors.textMuted }}>{stats.total_users_active} active today, avg {stats.avg_usage} msgs</p>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? '#0f172a' : '#e2e8f0' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: pct > 50 ? '#f59e0b' : t.color }} />
                      </div>
                      <span className="text-[11px] shrink-0" style={{ color: colors.textMuted }}>
                        {stats.users_at_limit}/{stats.total_users_active} users &middot; avg {stats.avg_usage} msgs
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatPerformanceCard({ data, isDark, colors, axisColor }) {
  if (!data) return null;
  const { today, week, trend_30d, phase_breakdown } = data;

  const fmtMs = (ms) => {
    if (!ms || ms === 0) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const todayVsWeek = (todayVal, weekVal) => {
    if (!weekVal || weekVal === 0) return null;
    const diff = Math.round(((todayVal - weekVal) / weekVal) * 100);
    if (Math.abs(diff) < 3) return { dir: 'flat', pct: 0 };
    return { dir: diff < 0 ? 'faster' : 'slower', pct: Math.abs(diff) };
  };

  const ttftTrend = todayVsWeek(today.avg_ttft_ms, week.avg_ttft_ms);
  const totalTrend = todayVsWeek(today.avg_total_ms, week.avg_total_ms);
  const totalPhase = (phase_breakdown.avg_phase1_ms || 0) + (phase_breakdown.avg_phase2_ms || 0) + (phase_breakdown.avg_phase3_ms || 0);

  const phases = [
    { label: 'DB Reads', ms: phase_breakdown.avg_phase1_ms, color: '#6366f1' },
    { label: 'Refs + Title', ms: phase_breakdown.avg_phase2_ms, color: '#f59e0b' },
    { label: 'Verses + Commentary', ms: phase_breakdown.avg_phase3_ms, color: '#10b981' },
  ];

  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }} data-testid="chat-performance-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-400" />
          <h3 className="text-sm font-medium" style={{ color: colors.textSecondary }}>Chat Performance</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] px-2 py-0.5 rounded-md" style={{ background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: '#818cf8' }}>
            {today.count} today / {week.count} this week
          </span>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Avg TTFT', value: fmtMs(today.avg_ttft_ms), trend: ttftTrend, sub: `P95: ${fmtMs(today.p95_ttft_ms)}` },
          { label: 'Avg Total', value: fmtMs(today.avg_total_ms), trend: totalTrend, sub: `P95: ${fmtMs(today.p95_total_ms)}` },
          { label: 'Prep Time', value: fmtMs(today.avg_prep_ms), sub: 'Before streaming' },
          { label: '7d Avg TTFT', value: fmtMs(week.avg_ttft_ms), sub: `P95: ${fmtMs(week.p95_ttft_ms)}` },
        ].map((kpi, i) => (
          <div key={i} className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }} data-testid={`perf-kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <Timer className="h-3 w-3" style={{ color: colors.textMuted }} />
              <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: colors.textMuted }}>{kpi.label}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold" style={{ color: colors.text }}>{kpi.value}</span>
              {kpi.trend && kpi.trend.dir !== 'flat' && (
                <span className="text-[10px] font-semibold" style={{ color: kpi.trend.dir === 'faster' ? '#10b981' : '#f43f5e' }}>
                  {kpi.trend.dir === 'faster' ? '\u2193' : '\u2191'}{kpi.trend.pct}%
                </span>
              )}
            </div>
            <span className="text-[10px]" style={{ color: colors.textMuted }}>{kpi.sub}</span>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {/* Trend Chart */}
        <div>
          <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Response Time Trend (30d)</p>
          {(trend_30d || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={trend_30d}>
                <defs>
                  <linearGradient id="ttftGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={isDark ? '#1e293b' : '#f1f5f9'} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: axisColor }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: axisColor }} width={40} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}s` : `${v}`} />
                <Tooltip content={({ payload, label }) => {
                  if (!payload?.length) return null;
                  return (
                    <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                      <p className="font-medium mb-1" style={{ color: colors.text }}>{label}</p>
                      {payload.map((p, i) => (
                        <p key={i} style={{ color: p.color }}>{p.name}: {fmtMs(p.value)}</p>
                      ))}
                      <p style={{ color: colors.textMuted }}>{payload[0]?.payload?.count} requests</p>
                    </div>
                  );
                }} />
                <Area type="monotone" dataKey="avg_total_ms" stroke="#6366f1" fill="url(#totalGrad)" strokeWidth={1.5} name="Total" dot={false} />
                <Area type="monotone" dataKey="avg_ttft_ms" stroke="#f59e0b" fill="url(#ttftGrad)" strokeWidth={2} name="TTFT" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-xs py-8 text-center" style={{ color: colors.textMuted }}>No performance data yet — send some chat messages!</p>}
        </div>

        {/* Phase Breakdown */}
        <div>
          <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Pre-Stream Phase Breakdown (7d avg)</p>
          <div className="space-y-3">
            {phases.map((p, i) => (
              <div key={i} className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="text-xs font-medium" style={{ color: colors.text }}>{p.label}</span>
                  </div>
                  <span className="text-xs font-bold tabular-nums" style={{ color: p.color }}>{fmtMs(p.ms)}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? '#0f172a' : '#e2e8f0' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: totalPhase > 0 ? `${Math.max((p.ms / totalPhase) * 100, 2)}%` : '0%', background: p.color }} />
                </div>
              </div>
            ))}
            {totalPhase > 0 && (
              <p className="text-[10px] text-right" style={{ color: colors.textMuted }}>Total prep: {fmtMs(totalPhase)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { admin } = useAdmin();
  const { isDark, colors } = useAdminTheme();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [charts, setCharts] = useState(null);
  const [topics, setTopics] = useState(null);
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [chatPerf, setChatPerf] = useState(null);
  const [topicsTab, setTopicsTab] = useState('popular');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [metricsRes, topicsRes, chartsRes, usageRes, botRes, perfRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard/metrics`, { withCredentials: true }),
          axios.get(`${API}/admin/dashboard/topics?days=30`, { withCredentials: true }),
          axios.get(`${API}/admin/dashboard/charts`, { withCredentials: true }),
          axios.get(`${API}/admin/dashboard/usage-analytics`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/admin/dashboard/bot-stats`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/admin/dashboard/chat-performance`, { withCredentials: true }).catch(() => null),
        ]);
        setMetrics(metricsRes.data);
        setTopics(topicsRes.data);
        setCharts(chartsRes.data);
        if (usageRes) setUsageAnalytics(usageRes.data);
        if (botRes) setBotStats(botRes.data);
        if (perfRes) setChatPerf(perfRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  const m = metrics || {};
  const c = charts || {};
  const axisColor = isDark ? '#475569' : '#94a3b8';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Dashboard</h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>Welcome back, {admin?.name}</p>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={m.registered_users ?? 0} sub={`${m.guest_users ?? 0} guests · ${m.subscriptions?.excluded_admin_users ?? 0} admin excl.`} color="#6366f1" onClick={() => navigate('/admin/users')} />
          <StatCard icon={CreditCard} label="Paying Subscribers" value={m.subscriptions?.paying?.total ?? 0} sub={`${m.subscriptions?.paying?.paid ?? 0} paid · ${m.subscriptions?.paying?.premium ?? 0} prem · ${m.subscriptions?.paying?.share ?? 0} share`} color="#22c55e" onClick={() => navigate('/admin/users?tier=subscribers')} />
          <StatCard icon={DollarSign} label="Est. MRR (real payments)" value={`$${m.monthly_revenue_estimate ?? 0}`} sub={`${m.subscriptions?.comp_active ?? 0} comped excluded`} color="#f59e0b" />
          <StatCard icon={Activity} label="DAU / WAU / MAU" value={m.active_users?.daily ?? 0} sub={`${m.active_users?.weekly ?? 0} weekly / ${m.active_users?.monthly ?? 0} monthly`} color="#ec4899" />
        </div>

        {/* New Users + More KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <NewUsersCard newUsers={m.new_users} />
          </div>
          <StatCard icon={TrendingUp} label="Conversion" value={`${m.conversion_rate ?? 0}%`} sub={`${m.trial_count ?? 0} trials issued`} color="#f59e0b" />
          <StatCard icon={MessageSquare} label="Avg Msgs/User" value={m.avg_messages_per_user ?? 0} color="#8b5cf6" />
        </div>

        {/* Install-CTA click sources — server-side tracking of every
            App Store / Play Store tap so we can see which surface
            drives real install intent. */}
        <InstallSourcesCard colors={colors} isDark={isDark} />

        {/* Charts Row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Subscription Distribution Donut */}
          <ChartCard title="Subscription Distribution" icon={CreditCard} iconColor="#6366f1">
            {(c.subscription_distribution || []).some(d => d.value > 0) ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie data={c.subscription_distribution} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={52} paddingAngle={2} strokeWidth={0}>
                      {(c.subscription_distribution || []).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {(c.subscription_distribution || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                      <span style={{ color: colors.textSecondary }}>{d.name}</span>
                      <span className="font-semibold" style={{ color: colors.text }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-xs" style={{ color: colors.textMuted }}>No data yet</p>}
          </ChartCard>

          {/* Messages Area Chart */}
          <ChartCard title="Messages (30 days)" icon={MessageSquare} iconColor="#8b5cf6">
            {(c.messages_30d || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={c.messages_30d}>
                  <defs>
                    <linearGradient id="msgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={false} axisLine={false} />
                  <YAxis tick={false} axisLine={false} width={0} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area type="monotone" dataKey="messages" stroke="#8b5cf6" fill="url(#msgGrad)" strokeWidth={2} name="Messages" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-xs" style={{ color: colors.textMuted }}>No data yet</p>}
          </ChartCard>

          {/* DAU Line Chart */}
          <ChartCard title="Daily Active Users" icon={Activity} iconColor="#ec4899">
            {(c.dau_trend || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={c.dau_trend}>
                  <XAxis dataKey="date" tick={false} axisLine={false} />
                  <YAxis tick={false} axisLine={false} width={0} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Line type="monotone" dataKey="users" stroke="#ec4899" strokeWidth={2} dot={false} name="Active Users" />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-xs" style={{ color: colors.textMuted }}>No data yet</p>}
          </ChartCard>
        </div>

        {/* Second charts row */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* User Growth */}
          <ChartCard title="User Signups (30 days)" icon={UserPlus} iconColor="#22c55e">
            {(c.signups_30d || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={c.signups_30d}>
                  <defs>
                    <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={isDark ? '#1e293b' : '#f1f5f9'} strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: axisColor }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: axisColor }} width={30} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area type="monotone" dataKey="signups" stroke="#22c55e" fill="url(#signupGrad)" strokeWidth={2} name="Signups" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-xs" style={{ color: colors.textMuted }}>No data yet</p>}
          </ChartCard>

          {/* Broadcast Engagement */}
          <ChartCard title="Broadcast Engagement" icon={TrendingUp} iconColor="#f59e0b">
            {(c.broadcast_engagement || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={c.broadcast_engagement}>
                  <CartesianGrid stroke={isDark ? '#1e293b' : '#f1f5f9'} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: axisColor }} />
                  <YAxis tick={{ fontSize: 10, fill: axisColor }} width={30} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Bar dataKey="dismissals" fill="#64748b" radius={[4, 4, 0, 0]} name="Dismissed" />
                  <Bar dataKey="acceptances" fill="#22c55e" radius={[4, 4, 0, 0]} name="Accepted" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs" style={{ color: colors.textMuted }}>No broadcast data yet</p>}
          </ChartCard>
        </div>

        {/* Usage Analytics */}
        {usageAnalytics && <UsageAnalyticsCard data={usageAnalytics} isDark={isDark} colors={colors} axisColor={axisColor} />}

        {/* Chat Performance Metrics */}
        <ChatPerformanceCard data={chatPerf} isDark={isDark} colors={colors} axisColor={axisColor} />

        {/* Bot Protection Stats */}
        {botStats && (
          <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }} data-testid="bot-protection-section">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-rose-400" />
                <h3 className="text-sm font-medium" style={{ color: colors.textSecondary }}>Bot Protection</h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="text-xs" style={{ color: colors.textMuted }}>Today</span>
                  <p className="text-lg font-bold" style={{ color: botStats.today_total > 0 ? '#f43f5e' : colors.text }}>{botStats.today_total}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs" style={{ color: colors.textMuted }}>All time</span>
                  <p className="text-lg font-bold" style={{ color: colors.text }}>{botStats.all_time_total}</p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {/* 7-day trend chart */}
              <div>
                <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Blocks (7 days)</p>
                {(botStats.trend_7d || []).some(d => d.blocks > 0) ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={botStats.trend_7d}>
                      <CartesianGrid stroke={isDark ? '#1e293b' : '#f1f5f9'} strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: axisColor }} />
                      <YAxis tick={{ fontSize: 10, fill: axisColor }} width={25} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Bar dataKey="blocks" fill="#f43f5e" radius={[3, 3, 0, 0]} name="Blocked" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs py-8 text-center" style={{ color: colors.textMuted }}>No blocks yet — your app is clean!</p>}
              </div>

              {/* Breakdown by type */}
              <div>
                <p className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>Today by Type</p>
                <div className="space-y-2.5">
                  {[
                    { key: 'honeypot', label: 'Honeypot Catches', color: '#f43f5e', desc: 'Bot registrations blocked' },
                    { key: 'velocity', label: 'Velocity Blocks', color: '#f97316', desc: 'Rapid-fire messages stopped' },
                    { key: 'guest_abuse', label: 'Guest Abuse', color: '#eab308', desc: 'IP-based guest limits hit' },
                    { key: 'content_filter', label: 'Content Filter', color: '#6366f1', desc: 'Off-topic/harmful blocked' },
                  ].map(t => (
                    <div key={t.key} className="rounded-xl px-3 py-2.5" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }} data-testid={`bot-type-${t.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                          <span className="text-xs font-medium" style={{ color: colors.text }}>{t.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs" style={{ color: colors.textMuted }}>{botStats.today?.[t.key] || 0} today</span>
                          <span className="text-sm font-bold" style={{ color: (botStats.all_time?.[t.key] || 0) > 0 ? t.color : colors.text }}>{botStats.all_time?.[t.key] || 0}</span>
                        </div>
                      </div>
                      <p className="text-[10px] mt-0.5 ml-4" style={{ color: colors.textMuted }}>{t.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent events */}
            {(botStats.recent_events || []).length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>Recent Events</p>
                <div className="space-y-1 max-h-[150px] overflow-y-auto">
                  {botStats.recent_events.map((ev, i) => (
                    <div key={i} className="flex items-center gap-3 px-2 py-1.5 rounded-lg text-xs" style={{ background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc' }}>
                      <span className="shrink-0 w-[52px]" style={{ color: colors.textMuted }}>{new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold" style={{
                        background: ev.type === 'honeypot' ? '#f43f5e20' : ev.type === 'velocity' ? '#f9731620' : ev.type === 'guest_abuse' ? '#eab30820' : '#6366f120',
                        color: ev.type === 'honeypot' ? '#f43f5e' : ev.type === 'velocity' ? '#f97316' : ev.type === 'guest_abuse' ? '#eab308' : '#6366f1',
                      }}>{ev.type}</span>
                      <span className="truncate" style={{ color: colors.textSecondary }}>
                        {ev.details?.user_id ? `User: ${ev.details.user_id.slice(0, 12)}...` :
                         ev.details?.ip ? `IP: ${ev.details.ip}` :
                         ev.details?.category ? `Category: ${ev.details.category}` :
                         ev.details?.value ? `Value: ${ev.details.value.slice(0, 20)}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Country Distribution */}
        <CountrySection countries={m.countries || []} onCountryClick={(country) => navigate(`/admin/users?country=${encodeURIComponent(country)}`)} />

        {/* Guest Analytics */}
        <GuestAnalyticsCard colors={colors} isDark={isDark} />

        {/* Daily Reflections Stats */}
        {m.reflections && (
          <div
            className="rounded-2xl p-5"
            style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
            data-testid="reflection-stats-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#14b8a618' }}>
                  <BookOpen className="h-4.5 w-4.5" style={{ color: '#14b8a6' }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Daily Reflections</h3>
                  {m.reflections.today_topic && (
                    <p className="text-xs" style={{ color: colors.textMuted }}>Today: {m.reflections.today_topic}</p>
                  )}
                </div>
              </div>
              <button
                className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: colors.accent, background: `${colors.accent}10` }}
                onClick={() => navigate('/admin/reflections')}
                data-testid="reflections-view-all-btn"
              >
                View All
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                <p className="text-xs" style={{ color: colors.textMuted }}>Generated</p>
                <p className="text-lg font-bold" style={{ color: '#14b8a6' }}>{m.reflections.total_reflections ?? 0}</p>
              </div>
              <div className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                <p className="text-xs" style={{ color: colors.textMuted }}>Shared</p>
                <p className="text-lg font-bold" style={{ color: '#6366f1' }}>{m.reflections.total_shares ?? 0}</p>
              </div>
              <div className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                <p className="text-xs" style={{ color: colors.textMuted }}>Discussions</p>
                <p className="text-lg font-bold" style={{ color: '#8b5cf6' }}>{m.reflections.total_discussions ?? 0}</p>
              </div>
              <div className="rounded-xl p-3" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                <p className="text-xs" style={{ color: colors.textMuted }}>Shares (7d)</p>
                <p className="text-lg font-bold" style={{ color: '#f59e0b' }}>{m.reflections.shares_last_7d ?? 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Bottom row KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={MessageSquare} label="Total Messages" value={m.total_messages ?? 0} color="#8b5cf6" />
          <StatCard icon={BookOpen} label="Conversations" value={m.total_conversations ?? 0} color="#14b8a6" />
          <StatCard icon={TrendingUp} label="Active Stripe" value={m.subscriptions?.active_stripe ?? 0} sub="Active subscriptions" color="#22c55e" />
          <StatCard icon={Users} label="Share Plans" value={m.share_plan?.active_plans ?? 0} sub={`${m.share_plan?.accepted_members ?? 0} members, ${m.share_plan?.pending_invites ?? 0} pending`} color="#10b981" onClick={() => navigate('/admin/users?tier=share')} />
        </div>

        {/* User Interests / Topics */}
      </div>
    </AdminLayout>
  );
}
